import { AuthSession } from "@server/models/auth.ts";

export const AuthSessionManager = {
  save,
  getAuthToken,
  getRefreshToken,
  getAuthSession,
  clear,
  getSnapshot,
  isCurrent,
  replace,
  subscribe,
};

export type AuthSessionSnapshot = {
  version: number;
  authToken: string | null;
  refreshToken: string | null;
};

let version = 0;
const listeners = new Set<() => void>();

const authTokenLocalStorageKey = "colmeia-auth-token";
const refreshAuthTokenLocalStorageKey = "colmeia-refresh-auth-token";

function save(session: AuthSession) {
  version++;
  write(session);
  notify();
}

function write(session: AuthSession) {
  localStorage.setItem(authTokenLocalStorageKey, session.authToken);
  localStorage.setItem(refreshAuthTokenLocalStorageKey, session.refreshToken);
}

function getAuthToken(): string | null {
  return localStorage.getItem(authTokenLocalStorageKey) ?? null;
}

function getRefreshToken(): string | null {
  return localStorage.getItem(refreshAuthTokenLocalStorageKey) ?? null;
}

function getAuthSession(): AuthSession | null {
  const result = AuthSession.safeParse({
    authToken: getAuthToken(),
    refreshToken: getRefreshToken(),
  });
  return result.success ? result.data : null;
}

function clear() {
  version++;
  localStorage.removeItem(authTokenLocalStorageKey);
  localStorage.removeItem(refreshAuthTokenLocalStorageKey);
  notify();
}

function getSnapshot(): AuthSessionSnapshot {
  return {
    version,
    authToken: getAuthToken(),
    refreshToken: getRefreshToken(),
  };
}

function isCurrent(snapshot: AuthSessionSnapshot): boolean {
  return snapshot.version === version;
}

function replace(session: AuthSession, snapshot: AuthSessionSnapshot): boolean {
  if (!isCurrent(snapshot) || getRefreshToken() !== snapshot.refreshToken) {
    return false;
  }
  write(session);
  return true;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}
